import { logger } from "$lib/utils/logger";
import type { DataPipelineEvent } from "./eventTypes";

const DEFAULT_MAX_SIZE = 200;

class EventQueue {
  private events: DataPipelineEvent[] = [];
  private maxSize: number;
  private droppedCount = 0;

  constructor(maxSize: number = DEFAULT_MAX_SIZE) {
    this.maxSize = maxSize;
  }

  enqueue(event: DataPipelineEvent) {
    if (this.events.length >= this.maxSize) {
      // Drop oldest event to make room
      this.events.shift();
      this.droppedCount++;
      if (this.droppedCount === 1) {
        logger.warn(
          `[EventQueue] Queue full (max=${this.maxSize}), dropping oldest events`
        );
      }
    }
    this.events.push(event);
  }

  dequeueBatch(batchSize: number): DataPipelineEvent[] {
    return this.events.splice(0, batchSize);
  }

  /** Dequeue events while cumulative JSON byte size stays under maxBytes.
   *  Always returns at least one event (caller handles oversize-single case). */
  dequeueBatchByBytes(maxBytes: number, maxCount: number): DataPipelineEvent[] {
    const batch: DataPipelineEvent[] = [];
    let bytes = 2; // "[]"
    while (this.events.length > 0 && batch.length < maxCount) {
      const next = this.events[0];
      const nextBytes =
        JSON.stringify(next).length + (batch.length > 0 ? 1 : 0); // +1 for comma
      if (batch.length > 0 && bytes + nextBytes > maxBytes) break;
      batch.push(this.events.shift()!);
      bytes += nextBytes;
    }
    return batch;
  }

  requeueBatch(batch: DataPipelineEvent[]) {
    // Only requeue up to the remaining capacity
    const capacity = this.maxSize - this.events.length;
    if (batch.length > capacity) {
      const dropped = batch.length - capacity;
      this.droppedCount += dropped;
      logger.warn(
        `[EventQueue] Requeue would exceed max (${this.maxSize}), dropping ${dropped} oldest events. Total dropped: ${this.droppedCount}`
      );
      batch = batch.slice(batch.length - capacity); // keep newest
    }
    this.events.unshift(...batch);
  }

  isEmpty(): boolean {
    return this.events.length === 0;
  }

  size(): number {
    return this.events.length;
  }
}

export { EventQueue, DEFAULT_MAX_SIZE };
